// Three axios instances, mirroring the bounce-dads pattern:
//   api      — public endpoints, always sends X-API-Key + optional auth bearer
//   authApi  — admin login endpoints (no JWT required, API key required)
//   adminApi — admin endpoints (JWT + API key; 401 -> clear + redirect)
import axios from 'axios';
import { downscaleImage, REFERENCE_MAX_EDGE_PX } from '../utils/downscale';
import type {
  FeaturedImage,
  Inquiry,
  InquiryCreate,
  Order,
  OrderCreatePayload,
  OrderRequestResponse,
  OrderRequestSubmission,
  OrderUpdatePayload,
  PaymentMethodOption,
  PrivateLibrary,
  PrivateLibraryUnlockResponse,
  Product,
  Variant,
} from '../types';

// In production this is `/api` on the site's own domain, which Amplify
// forwards to the API, so browsers -- the shop's and the admin's -- never
// talk to the API host directly. Locally it's the backend's own address.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';
const DEBUG_LOGGING = process.env.REACT_APP_DEBUG_LOGGING === 'true';

const debugLog = (msg: string) => {
  if (DEBUG_LOGGING) console.log(`[api] ${msg}`);
};

// ---------- Instances ----------

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Reason codes the login page reads back to explain why it bounced you.
export type AuthBounceReason = 'expired' | 'forbidden' | 'unconfigured';

/**
 * Turn an axios error into something worth showing a person.
 *
 * Raw axios messages ("Request failed with status code 503") say nothing
 * about what to do, so the cases we can recognise get real explanations.
 */
export const describeApiError = (error: any, fallback = 'Something went wrong.'): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  if (status === 503) {
    // The backend raises this when Cognito env vars are missing.
    return typeof detail === 'string' && detail.includes('Cognito')
      ? 'The server is missing its authentication configuration. If you are running locally, restart the backend so it picks up backend/.env.'
      : 'The server is temporarily unavailable. Please try again shortly.';
  }
  if (status === 429) return typeof detail === 'string' ? detail : 'Too many requests — please wait a moment.';
  if (status === 413) return 'That file is too large to upload.';
  // Customers see this too, so it can't ask them about a backend. A bare
  // network error is also what a rejected CORS preflight looks like from
  // JavaScript, which is worth remembering when one appears out of nowhere.
  if (error?.code === 'ERR_NETWORK') {
    return 'Could not reach the server — check your connection and try again.';
  }
  if (typeof detail === 'string') return detail;
  return error?.message ?? fallback;
};

/**
 * Drop the stored session and send the admin back to the login page.
 *
 * A hard navigation is used rather than router state because interceptors
 * live outside the React tree, so the reason and the page the admin was
 * trying to reach are handed over via sessionStorage instead.
 */
const bounceToLogin = (reason: AuthBounceReason) => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');

  const here = window.location.pathname + window.location.search;
  if (here.startsWith('/admin/login')) return; // already there; don't loop

  sessionStorage.setItem('adminAuthReason', reason);
  if (here.startsWith('/admin')) sessionStorage.setItem('adminReturnTo', here);
  window.location.replace('/admin/login');
};

// 401 = no/expired/invalid token. 403 = valid token without an admin group.
// Both mean "you are not signed in as an admin", so both go back to login
// rather than leaving a broken page with an error on it.
const isAuthFailure = (status?: number) => status === 401 || status === 403;

// Public api: API key + optional JWT
api.interceptors.request.use((config) => {
  config.headers['X-API-Key'] = API_KEY;
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    // Only bounce inside the admin area — a 401 from a public endpoint must
    // never yank a customer to an admin login screen.
    if (isAuthFailure(error.response?.status) && window.location.pathname.startsWith('/admin')) {
      bounceToLogin(error.response.status === 403 ? 'forbidden' : 'expired');
    }
    return Promise.reject(error);
  }
);

// Auth api: API key only (used for login)
authApi.interceptors.request.use((config) => {
  config.headers['X-API-Key'] = API_KEY;
  return config;
});

// Admin api: API key + JWT required
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  config.headers['X-API-Key'] = API_KEY;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    debugLog('adminApi request: JWT attached');
  } else {
    debugLog('adminApi request: NO JWT');
  }
  return config;
});

adminApi.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error.response?.status;
    if (isAuthFailure(status)) {
      debugLog(`adminApi ${status} — returning to login`);
      bounceToLogin(status === 403 ? 'forbidden' : 'expired');
    }
    return Promise.reject(error);
  }
);

// ---------- Products (M2) ----------

export const getProducts = async (
  activeOnly = true,
  includePrivate = false
): Promise<Product[]> => {
  const { data } = await api.get('/products', {
    params: { active_only: activeOnly, include_private: includePrivate },
  });
  return data;
};

export const getProduct = async (productId: string): Promise<Product> => {
  const { data } = await api.get(`/products/${productId}`);
  return data;
};

export const createProduct = async (product: Partial<Product>) => {
  const { data } = await adminApi.post('/products', product);
  return data;
};

export const updateProduct = async (productId: string, product: Partial<Product>) => {
  const { data } = await adminApi.patch(`/products/${productId}`, product);
  return data;
};

export const deleteProduct = async (productId: string) => {
  const { data } = await adminApi.delete(`/products/${productId}`);
  return data;
};

// ---------- Variants (M2) ----------

export const addVariant = async (productId: string, variant: Partial<Variant>): Promise<Product> => {
  const { data } = await adminApi.post(`/products/${productId}/variants`, variant);
  return data;
};

export const updateVariant = async (
  productId: string,
  sku: string,
  variant: Partial<Variant>
): Promise<Product> => {
  const { data } = await adminApi.patch(`/products/${productId}/variants/${sku}`, variant);
  return data;
};

export const deleteVariant = async (productId: string, sku: string): Promise<Product> => {
  const { data } = await adminApi.delete(`/products/${productId}/variants/${sku}`);
  return data;
};

export const setInventory = async (sku: string, inStock: boolean) => {
  const { data } = await adminApi.put(`/inventory/${sku}`, { in_stock: inStock });
  return data as { sku: string; in_stock: boolean };
};

// ---------- Inventory (M2) ----------

export const getInventoryBulk = async (skus: string[]) => {
  const { data } = await api.post('/inventory/bulk', { skus });
  return data as { stock: Record<string, boolean> };
};

// ---------- Uploads (M2 — admin) ----------
//
// Files go browser -> S3 directly, using a URL the API presigns for us.
// They used to be POSTed to the API as multipart, but that path runs
// through API Gateway and Lambda, and Lambda refuses request payloads over
// 6 MB — API Gateway base64-encodes the body first, so the real ceiling was
// about 4.5 MB and anything larger came back as an unexplained 413. Keeping
// the bytes off that path removes the limit rather than working around it.

interface Presigned {
  file_id: string;
  upload_url: string;
  content_type: string;
  cache_control: string;
  s3_key: string;
  s3_url: string;
}

/**
 * PUT the file to S3.
 *
 * Deliberately a bare axios call rather than one of the instances above:
 * their interceptors attach X-API-Key and Authorization, and an
 * Authorization header on a presigned request makes S3 reject the
 * signature. Content-Type and Cache-Control are both covered by that
 * signature, so they're echoed back exactly as the API gave them rather
 * than derived from the File here.
 */
const putToS3 = async (
  uploadUrl: string,
  file: File,
  presigned: Presigned | HeaderPair
) => {
  try {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': presigned.content_type,
        'Cache-Control': presigned.cache_control,
      },
    });
  } catch (e: any) {
    // S3 talks XML and a rejected preflight reaches the browser as a bare
    // "Network Error" with no detail at all — neither is worth showing a
    // customer, and the raw version sent one chasing their connection when
    // the real cause was a header missing from the bucket's CORS rules.
    const status = e?.response?.status;
    if (status === 413) {
      throw new Error(`${file.name} is too large to upload.`);
    }
    if (status === 403) {
      throw new Error(
        `Upload of ${file.name} was rejected. The link may have expired — reload the page and try again.`
      );
    }
    throw new Error(
      `Could not upload ${file.name}. Check your connection and try again.`
    );
  }
};

/** The two signed headers, for callers whose presign response differs. */
interface HeaderPair {
  content_type: string;
  cache_control: string;
}

/**
 * Admin upload: product images and invoice proofs.
 *
 * Deliberately not downscaled, unlike the carousel and custom-request
 * paths. Product photos get zoomed by customers, and an invoice proof is
 * the record of what was agreed — both are cases where throwing away
 * detail to save a few hundred kilobytes is the wrong trade.
 */
export const uploadImage = async (
  file: File,
  category: 'product' | 'inquiry',
  metadata?: Record<string, string>
) => {
  const { data: presigned } = await adminApi.post<Presigned>('/uploads/presign', {
    filename: file.name,
    category,
    product_id: metadata?.product_id,
    description: metadata?.description,
  });
  await putToS3(presigned.upload_url, file, presigned);
  // Confirm flips the metadata row from pending to active and records the
  // size S3 actually received.
  const { data } = await api.post(`/uploads/${presigned.file_id}/confirm`);
  return data as { file_id: string; s3_url: string };
};

/**
 * Throw away a custom-request photo that never made it onto a request.
 *
 * Uses fetch with keepalive rather than one of the axios instances: this
 * is fired as the page is being left, and a normal XHR is cancelled the
 * moment the document goes away. sendBeacon can't be used — it only
 * issues POSTs. Failures are ignored; the request is best-effort by
 * nature, and there is no one left to show an error to.
 */
export const discardInquiryImage = (fileId: string): void => {
  fetch(`${API_BASE_URL}/uploads/inquiry-image/${fileId}`, {
    method: 'DELETE',
    keepalive: true,
    headers: { 'X-API-Key': API_KEY },
  }).catch(() => undefined);
};

// Public-side image upload tied to the custom-request form. No admin
// auth required; backend hard-codes category=inquiry.
export const uploadInquiryImage = async (original: File) => {
  // Shrunk, but far less than carousel photos: the shop quotes from these,
  // so stitch detail and thread shades have to survive. Mostly this is
  // about the customer not waiting on a 15 MB upload over phone data.
  const file = await downscaleImage(original, REFERENCE_MAX_EDGE_PX);

  const { data: presigned } = await api.post<Presigned>('/uploads/inquiry-image/presign', {
    filename: file.name,
  });
  await putToS3(presigned.upload_url, file, presigned);
  const { data } = await api.post(`/uploads/${presigned.file_id}/confirm`);
  return data as { file_id: string; s3_url: string };
};

// ---------- Auth (admin) ----------

export const adminLogin = async (credentials: { username: string; password: string }) => {
  const { data } = await authApi.post('/auth/login', credentials);
  return data as { access_token: string; id_token: string; refresh_token: string };
};

export const adminMe = async () => {
  const { data } = await adminApi.get('/auth/me');
  return data;
};

// ---------- Checkout ----------
//
// Submitting the cart creates a pending order for the shop to review and
// invoice. No card is charged here — payment happens via the Stripe payment
// link on the invoice, or in person.

export const submitOrderRequest = async (
  payload: OrderRequestSubmission
): Promise<OrderRequestResponse> => {
  const { data } = await api.post('/checkout/request', payload);
  return data;
};

// ---------- Orders (M3) ----------

export const getOrderByEmail = async (email: string, orderNumber: string): Promise<Order> => {
  const { data } = await api.get('/orders/by-email', { params: { email, order_number: orderNumber } });
  return data;
};

// Public: the invoice link carries an unguessable token instead of the
// customer's email, so nothing identifying sits in the URL.
export const getOrderByInvoiceToken = async (token: string): Promise<Order> => {
  const { data } = await api.get('/orders/by-invoice-token', { params: { token } });
  return data;
};

export const adminGetOrders = async (status?: string): Promise<Order[]> => {
  const { data } = await adminApi.get('/orders', { params: status ? { status } : {} });
  return data;
};

export const adminGetOrder = async (orderId: string): Promise<Order> => {
  const { data } = await adminApi.get(`/orders/${orderId}`);
  return data;
};

export const adminCreateOrder = async (payload: OrderCreatePayload): Promise<Order> => {
  const { data } = await adminApi.post('/orders', payload);
  return data;
};

export const adminUpdateOrder = async (
  orderId: string,
  patch: OrderUpdatePayload
): Promise<Order> => {
  const { data } = await adminApi.patch(`/orders/${orderId}`, patch);
  return data;
};

export const adminDeleteOrder = async (orderId: string): Promise<void> => {
  await adminApi.delete(`/orders/${orderId}`);
};

// With sendEmail false the invoice is built — number, date, customer link,
// Stripe link — and nothing is emailed, for a customer who's getting the
// link by text or who has no email address on file.
export const adminSendInvoice = async (
  orderId: string,
  options: {
    sampleImageUrl?: string;
    paymentMethods?: string[];
    sendEmail?: boolean;
  } = {}
): Promise<Order> => {
  const { data } = await adminApi.post(`/orders/${orderId}/send-invoice`, {
    sample_image_url: options.sampleImageUrl,
    payment_methods: options.paymentMethods,
    send_email: options.sendEmail ?? true,
  });
  return data;
};

// Templates an admin can send by hand from an order.
export const adminGetEmailTemplates = async (): Promise<
  { value: string; label: string }[]
> => {
  const { data } = await adminApi.get('/orders/email-templates');
  return data;
};

// Sends regardless of the order's "don't email this customer" setting —
// that silences automatic mail, and this is an explicit choice.
// `template` comes straight from getEmailTemplates — the status emails
// name their status in the value ("status:shipped"), so there's nothing
// else to pass.
export const adminSendOrderEmail = async (
  orderId: string,
  template: string
): Promise<{ sent: boolean; to?: string[] }> => {
  const { data } = await adminApi.post(`/orders/${orderId}/send-email`, {
    template,
  });
  return data;
};

export const adminMarkOrderPaid = async (
  orderId: string,
  paymentMethod?: string
): Promise<Order> => {
  const { data } = await adminApi.post(`/orders/${orderId}/mark-paid`, {
    payment_method: paymentMethod,
  });
  return data;
};

// ---------- Inquiries (M4) ----------

export const submitInquiry = async (payload: InquiryCreate): Promise<Inquiry> => {
  const { data } = await api.post('/inquiries', payload);
  return data;
};

export const adminGetInquiries = async (status?: string): Promise<Inquiry[]> => {
  const { data } = await adminApi.get('/inquiries', { params: status ? { status } : {} });
  return data;
};

export const adminGetInquiry = async (inquiryId: string): Promise<Inquiry> => {
  const { data } = await adminApi.get(`/inquiries/${inquiryId}`);
  return data;
};

export const adminUpdateInquiry = async (
  inquiryId: string,
  patch: { status?: Inquiry['status']; internal_notes?: string }
): Promise<Inquiry> => {
  const { data } = await adminApi.patch(`/inquiries/${inquiryId}`, patch);
  return data;
};

// Takes the reference photos with it — they exist only for this request.
export const adminDeleteInquiry = async (inquiryId: string): Promise<void> => {
  await adminApi.delete(`/inquiries/${inquiryId}`);
};

export const adminConvertInquiryToOrder = async (
  inquiryId: string,
  payload: OrderCreatePayload
): Promise<Order> => {
  const { data } = await adminApi.post(
    `/inquiries/${inquiryId}/convert-to-order`,
    payload
  );
  return data;
};

// ---------- Settings ----------

export const getPublicSettings = async () => {
  const { data } = await api.get('/admin/public-settings');
  return data;
};

export const adminGetSettings = async () => {
  const { data } = await adminApi.get('/admin/settings');
  return data;
};

export const adminUpdateSetting = async (key: string, value: any) => {
  const { data } = await adminApi.patch(`/admin/settings/${key}`, { value });
  return data;
};

// ---------- Featured items (home carousel) ----------
//
// These live directly in S3 under featured-items/, so the S3 key is the id.

/**
 * The home-page gallery.
 *
 * Read from a static manifest on the CDN first, because the API route
 * does an S3 listing and a settings read behind a Lambda that may have to
 * boot — measured at 5.4 seconds cold, for a list that changes only when
 * the shop edits it. The manifest is rewritten on every change, so the
 * only cost is up to a minute of staleness after one.
 *
 * Falls back to the API whenever the manifest can't be read: missing,
 * blocked, or a bucket with no CDN in front of it. That degrades to
 * exactly the old behaviour rather than to an empty carousel.
 */
export const getFeaturedImages = async (): Promise<FeaturedImage[]> => {
  const cdn = process.env.REACT_APP_CDN_URL;
  if (cdn) {
    try {
      const res = await fetch(`${cdn.replace(/\/$/, '')}/featured-manifest.json`);
      if (res.ok) {
        const manifest = await res.json();
        if (Array.isArray(manifest)) return manifest;
      }
    } catch {
      // Fall through to the API.
    }
  }
  const { data } = await api.get('/featured');
  return data;
};

// Rebuilds the manifest after a direct-to-S3 upload, which the API never
// sees. Failure is non-fatal: the manifest just stays stale for a minute.
export const adminRefreshFeaturedManifest = async (): Promise<void> => {
  await adminApi.post('/featured/refresh').catch(() => undefined);
};

export const adminUploadFeatured = async (original: File): Promise<FeaturedImage> => {
  // Carousel photos are shrunk before upload — see downscaleImage. Done
  // before presigning, because the resize changes the extension to .jpg
  // and the backend signs a content type from it.
  const file = await downscaleImage(original);

  const { data: presigned } = await adminApi.post<
    HeaderPair & { key: string; upload_url: string; url: string }
  >('/featured/presign', { filename: file.name });

  await putToS3(presigned.upload_url, file, presigned);

  // No confirm step here: the listing reads the bucket directly, so the
  // object landing *is* the record of it. Describe what we just wrote so
  // the admin grid updates without a refetch.
  return {
    key: presigned.key,
    url: presigned.url,
    filename: file.name,
    size: file.size,
    uploaded_at: new Date().toISOString(),
  };
};

export const adminDeleteFeatured = async (key: string): Promise<void> => {
  await adminApi.delete('/featured', { params: { key } });
};

// Carousel order. S3 has no notion of one, so the arrangement is stored
// separately as a list of keys — see the backend router for why.
export const adminSetFeaturedOrder = async (keys: string[]): Promise<void> => {
  await adminApi.put('/featured/order', { keys });
};

// ---------- Payment methods ----------

// Public: the customer Invoice page needs each method's label and details to
// render the "how to pay" panel.
export const getPaymentMethods = async (): Promise<PaymentMethodOption[]> => {
  const { data } = await api.get('/admin/payment-methods');
  return data;
};

export const adminSavePaymentMethods = async (methods: PaymentMethodOption[]) => {
  const { data } = await adminApi.patch('/admin/settings/payment_methods', {
    value: methods,
  });
  return data;
};

// ---------- Private libraries ----------

export const adminListLibraries = async (): Promise<PrivateLibrary[]> => {
  const { data } = await adminApi.get('/admin/private-libraries');
  return data;
};

export const adminCreateLibrary = async (payload: {
  slug: string;
  name: string;
  description?: string;
  password: string;
}): Promise<PrivateLibrary> => {
  const { data } = await adminApi.post('/admin/private-libraries', payload);
  return data;
};

export const adminUpdateLibrary = async (
  slug: string,
  patch: { name?: string; description?: string; password?: string; visible?: boolean }
): Promise<PrivateLibrary> => {
  const { data } = await adminApi.patch(`/admin/private-libraries/${slug}`, patch);
  return data;
};

export const adminDeleteLibrary = async (slug: string) => {
  await adminApi.delete(`/admin/private-libraries/${slug}`);
};

export const listLibrariesPublic = async (): Promise<PrivateLibrary[]> => {
  const { data } = await api.get('/private-libraries');
  return data;
};

export const getLibraryMeta = async (slug: string): Promise<PrivateLibrary> => {
  const { data } = await api.get(`/private-libraries/${slug}`);
  return data;
};

export const unlockLibrary = async (
  slug: string,
  password: string
): Promise<PrivateLibraryUnlockResponse> => {
  const { data } = await api.post(`/private-libraries/${slug}/unlock`, { password });
  return data;
};

export const getLibraryProducts = async (slug: string, token: string): Promise<Product[]> => {
  const { data } = await api.get(`/private-libraries/${slug}/products`, {
    headers: { 'X-Library-Token': token },
  });
  return data;
};

export default api;
